import React from 'react';
import { useAPI } from 'contexts/APIProvider';
import { Box, Typography, Paper, Chip } from '@mui/material';
import AdminNurseDetailContext from 'pages/AdminNurseDetail/context';
import { Experience as ExperienceType } from 'types/types';
import DocumentDisplay from './components/DocumentDisplay';
import NurseDocumentFile from 'logic/NurseDocumentFile';
import Loading from 'components/Loading';
import { useParams } from 'react-router-dom';

const AdminNurseExperience: React.FC = () => {
    const { nurse } = React.useContext(AdminNurseDetailContext);
    const params = useParams();
    const { api } = useAPI();
    const [files, setFiles] = React.useState<NurseDocumentFile[]>([]);
    const [loading, setLoading] = React.useState<boolean>(true);

    const experienceType = params.experienceType as 'implant' | 'orthodontics';
    const fieldName = `${experienceType}_experience` as 'implant_experience' | 'orthodontics_experience';
    const experienceValue = nurse?.[fieldName] as ExperienceType;

    // Fetch certificate documents if experience is certified
    const fetchDocuments = React.useCallback(async () => {
        if (experienceValue !== 'certified') {
            setLoading(false);
            return;
        }
        
        try {
            setLoading(true);
            const documentType = experienceType === 'implant' ? 'implant_certificate' : 'orthodontics_certificate';
            const documents = await api.listDocuments({ 
                nurse_id: nurse.nurse_id, 
                doc_type: documentType 
            });
            setFiles(documents);
        } catch (error) {
            console.error('Error fetching documents:', error);
        } finally {
            setLoading(false);
        }
    }, [api, experienceType, experienceValue, nurse.nurse_id]);

    React.useEffect(() => {
        if (nurse) {
            fetchDocuments();
        }
    }, [fetchDocuments, nurse]);

    const getExperienceLabel = () => {
        switch (experienceValue) {
            case 'no':
                return 'No experience';
            case 'yes':
                return 'Has experience';
            case 'certified':
                return 'Has experience and is certified';
            default:
                return 'Not specified';
        }
    };

    const getExperienceColor = () => {
        switch (experienceValue) {
            case 'no':
                return 'default';
            case 'yes':
                return 'primary';
            case 'certified':
                return 'success';
            default:
                return 'default';
        }
    };

    if (loading) {
        return <Loading />;
    }

    return (
        <Box sx={{ p: 2, mb: 2 }}>
            <Typography variant="h5" gutterBottom>
                {experienceType === 'implant' ? 'Implant' : 'Orthodontics'} Experience
            </Typography>
            
            <Box sx={{ mb: 2 }}>
                <Chip 
                    label={getExperienceLabel()} 
                    color={getExperienceColor()}
                    sx={{ fontSize: '1rem', py: 1, px: 1 }}
                />
            </Box>

            {experienceValue === 'certified' && (
                <Box>
                    <Typography variant="h6" gutterBottom>
                        Certification Documents
                    </Typography>
                    <DocumentDisplay files={files} />
                </Box>
            )}
        </Box>
    );
};

export default AdminNurseExperience;